/**
 * Letter-first onboarding — who the letter is for.
 *
 * One name is enough to start, but a letter can address several people at
 * once ("Kayla and Allie — my children"), so "Add another person" appends
 * more name/relationship pairs. Continue creates the draft letter with its
 * structured recipient rows and hands the id to the canvas.
 */
import { useNavigation } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import {
  letterToLovedOnesService,
  LetterRecipientRec,
} from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterRecipient');

const RELATIONSHIPS = ['My spouse', 'My child', 'My parent', 'A friend', 'Someone else'];

type RecipientDraft = { name: string; relationship: string };

export const LetterRecipientScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [recipients, setRecipients] = useState<RecipientDraft[]>([
    { name: '', relationship: '' },
  ]);
  const [saving, setSaving] = useState(false);

  const setRecipient = (index: number, patch: Partial<RecipientDraft>) => {
    setRecipients((list) =>
      list.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const addRecipient = () => {
    setRecipients((list) => [...list, { name: '', relationship: '' }]);
  };

  const removeRecipient = (index: number) => {
    setRecipients((list) => list.filter((_, i) => i !== index));
  };

  const validRecipients: LetterRecipientRec[] = recipients
    .filter((r) => r.name.trim().length > 1)
    .map((r) => ({ name: r.name.trim(), relationship: r.relationship || undefined }));

  const canContinue = validRecipients.length > 0 && !saving;

  const handleContinue = async () => {
    if (!canContinue) return;
    try {
      setSaving(true);
      const letter = await letterToLovedOnesService.createLetterToLovedOnes({
        letterContent: '',
        status: 'draft',
        recipients: validRecipients,
      });
      logger.debug('Draft letter created:', letter.id);
      navigation.replace('LetterCanvas', { letterId: letter.id });
    } catch (error) {
      logger.error('Failed to create draft letter:', error);
      Alert.alert('Error', 'Failed to start your letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Your letter</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
          <View style={styles.orionRow}>
            <Image
              source={require('../../assets/icons/chat.png')}
              style={styles.orionIcon}
            />
            <Text style={styles.orionText}>
              Let's start with one person. Who would you most want to hear from
              you?
            </Text>
          </View>

          {recipients.map((recipient, index) => (
            <View key={index} style={styles.card}>
              {recipients.length > 1 && (
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => removeRecipient(index)}
                  accessibilityRole="button"
                  accessibilityLabel={`Remove person ${index + 1}`}
                >
                  <Text style={styles.removeButtonText}>Remove</Text>
                </TouchableOpacity>
              )}
              <Text style={styles.label}>Their name</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. Maya"
                placeholderTextColor="#A0AEC0"
                value={recipient.name}
                onChangeText={(name) => setRecipient(index, { name })}
                accessibilityLabel={`Name of person ${index + 1}`}
              />
              <Text style={styles.label}>Who are they to you?</Text>
              <View style={styles.chipsRow}>
                {RELATIONSHIPS.map((rel) => {
                  const selected = recipient.relationship === rel;
                  return (
                    <TouchableOpacity
                      key={rel}
                      style={[styles.chip, selected && styles.chipSelected]}
                      onPress={() => setRecipient(index, { relationship: selected ? '' : rel })}
                      accessibilityRole="button"
                      accessibilityLabel={rel}
                      accessibilityState={{ selected }}
                    >
                      <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
                        {rel}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          ))}

          <TouchableOpacity
            style={styles.addButton}
            onPress={addRecipient}
            accessibilityRole="button"
            accessibilityLabel="Add another person"
            accessibilityHint="Double tap to write this same letter to one more person"
          >
            <Text style={styles.addButtonText}>+ Add another person</Text>
          </TouchableOpacity>

          <Text style={styles.footnote}>
            One letter can be for more than one person — "Kayla and Allie" is
            fine. You can write to others later. One is enough to start.
          </Text>

          <TouchableOpacity
            style={[styles.continueButton, !canContinue && styles.continueDisabled]}
            onPress={handleContinue}
            disabled={!canContinue}
            accessibilityRole="button"
            accessibilityLabel="Continue"
            accessibilityState={{ disabled: !canContinue }}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.continueText}>Continue</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  orionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
    marginBottom: ms(16),
  },
  orionIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#29BFC0',
  },
  orionText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    lineHeight: ms(21, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    padding: ms(16),
    marginBottom: ms(12),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  removeButton: {
    alignSelf: 'flex-end',
  },
  removeButtonText: {
    fontSize: ms(13, 0.3),
    color: '#E85C5C',
    fontWeight: '600',
  },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(6),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    borderWidth: ms(1),
    borderColor: '#E5E9F0',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    fontSize: ms(16, 0.3),
    color: '#2C4364',
    marginBottom: ms(8),
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: ms(8),
  },
  chip: {
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#D8DBDD',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: ms(14),
    paddingVertical: ms(8),
  },
  chipSelected: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  chipText: {
    fontSize: ms(13, 0.3),
    color: '#2C4364',
    fontWeight: '600',
  },
  chipTextSelected: {
    color: '#FFFFFF',
  },
  addButton: {
    alignItems: 'center',
    paddingVertical: ms(12),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginBottom: ms(12),
  },
  addButtonText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginBottom: ms(16),
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
  },
  continueDisabled: {
    opacity: 0.5,
  },
  continueText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LetterRecipientScreen;
